/* eslint-disable @stylistic/brace-style */
/* eslint-disable @stylistic/semi */
import React from 'react';

import { WeatherMoonIcon, WeatherSunIcon } from '@vlabs/icons';
import { useAppTheme } from '@vlabs/shared/hooks';
import type { IconButtonProps } from '@vlabs/uikit/components/controls/button/icon-button';
import { IconButton } from '@vlabs/uikit/components/controls/button/icon-button';
import type { UikitThemeT } from '@vlabs/uikit/hooks/useTheme';
import type { WithDataTestid } from '@vlabs/uikit/types';

import type { NamedOption } from './utils';
import { getCurrentOption, getNextOption } from './utils';
import { buildTestid } from '../../../lib/utils';
import { navbarPanelTranslation } from '../i18n';

export interface ThemeOption extends NamedOption<UikitThemeT> {
  label: string;
  icon: React.ReactElement;
}

export const uikitThemeOptions: ThemeOption[] = [
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  { value: 'light', get label() { return navbarPanelTranslation('theme_light') }, icon: <WeatherMoonIcon /> },
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  { value: 'dark', get label() { return navbarPanelTranslation('theme_dark') }, icon: <WeatherSunIcon /> },
];

export interface ThemeButtonProps extends
  WithDataTestid,
  Omit<IconButtonProps, 'onClick'> {}

const displayName = 'ThemeButton';
const testid = buildTestid(displayName);

function ThemeButton({
  'data-testid': dataTestId = undefined,
  ...props
}: ThemeButtonProps): React.JSX.Element {
  const { theme, setTheme } = useAppTheme();
  const nextTheme = getNextOption(theme, uikitThemeOptions);
  const { option: currentThemeOption } = getCurrentOption(theme, uikitThemeOptions);

  return (
    <IconButton
      icon={currentThemeOption?.icon}
      kind="secondary"
      onClick={() => setTheme(nextTheme.value)}
      shape="square"
      title={currentThemeOption.label}
      variant="dimmed"
      {...testid(dataTestId)}
      {...props}
    />
  );
}

ThemeButton.displayName = displayName;

export { ThemeButton };
